import { SelectedMetadataFields } from '@modules/ManageUnifiedFields/types';
import { SelectMetadataDataType } from '@modules/common/components/SelectMetadataDataType';
import { UnifiedFieldIcon } from '@modules/common/components/UnifiedFieldIcon';
import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Divider,
  FormLabel,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { MetadataDataType } from 'generated/graphql';
import { SelectMetadataFieldAutoComplete } from '../SelectMetadataFieldAutoComplete';

interface CreateNewUnifiedFieldFormProps {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  setReferencedKeys: (referencedKeys: string[]) => void;
  setShouldHideReferencedKeys: (shouldHideReferencedKeys: boolean) => void;
  type: MetadataDataType | undefined;
  setType: (type: MetadataDataType) => void;
  selectedMetadataField: SelectedMetadataFields;
}

export const CreateNewUnifiedFieldForm = ({
  name,
  setName,
  description,
  setDescription,
  setReferencedKeys,
  setShouldHideReferencedKeys,
  type,
  setType,
  selectedMetadataField,
}: CreateNewUnifiedFieldFormProps) => {
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      sx={{
        p: 2,
      }}
      width="100%"
    >
      <Stack direction="row" spacing={2}>
        <Box width="60%">
          <FormLabel required component="legend">
            Field Name
          </FormLabel>
          <OutlinedInput
            required
            placeholder="Enter a name for this Unified Field"
            fullWidth
            size="medium"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>
        <Box flex={1}>
          <FormLabel required component="legend">
            Field Type
          </FormLabel>
          <SelectMetadataDataType value={type} onChange={setType} />
        </Box>
      </Stack>
      <FormLabel required component="legend" sx={{ mt: 2 }}>
        Field Description
      </FormLabel>
      <OutlinedInput
        required
        placeholder="Enter a description for this Unified Field"
        fullWidth
        size="medium"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Stack direction="row" alignItems="center" mt={2} spacing={1}>
        <Checkbox
          defaultChecked
          onChange={(_, checked) => setShouldHideReferencedKeys(checked)}
        />
        <Typography>Mark the fields being unified as invisible</Typography>
        <Tooltip
          title={
            <Typography variant="caption">
              When marking as invisible, Unified Fields de-clutter the
              organization's list of similar fields, enhancing operational
              efficiency and fostering a streamlined workspace.
            </Typography>
          }
        >
          <InfoOutlined />
        </Tooltip>
      </Stack>
      <Divider variant="fullWidth" sx={{ my: 3 }} />
      <Stack direction="row" spacing={1} my={1}>
        <UnifiedFieldIcon />
        <Typography variant="subtitle2" color="text.secondary">
          Unified metadata fields across integrations
        </Typography>
      </Stack>
      <SelectMetadataFieldAutoComplete
        setReferencedKeys={setReferencedKeys}
        value={selectedMetadataField}
      />
    </Box>
  );
};
